import {
  type MarketingMedia,
  approvedImage,
  approvedVideo,
} from '../marketing-media';
import {
  captureSpecifications,
  editorSpecifications,
  generationSpecifications,
} from './spec-content';
import type { ProductPageContent } from './types';

const captureFeatures = [
  [
    'multi-person-capture',
    'Multi-person capture',
    'Capture up to 4 people in a single pass',
  ],
  [
    'camera-capture-or-upload',
    'Camera capture or upload',
    'Camera position, angle, and lens automatically. Or upload your own from production',
  ],
  ['faces', 'Faces', 'Facial performance capture in same pass as body'],
  ['scale', 'Scale', 'Capture relative scale of people in space'],
  [
    'hands-wrists-fingers',
    'Hands, wrists, & fingers',
    'Full hand articulation most capture tools drop.',
  ],
  [
    'level-changes',
    'Level changes',
    'Stairs to hills, ground contact that holds through height changes.',
  ],
  [
    'batch-capture',
    'Batch Capture',
    'Capture up to 100 clips at once for production scale',
  ],
  ['realtime', 'Realtime', 'Capture performances and retarget in realtime'],
] as const;

const captureClips: Record<string, string> = {
  'multi-person-capture':
    '01_performance-capture_features_multi-person-capture.webm',
  'camera-capture-or-upload':
    '02_performance-capture_features_camera-capture.webm',
  faces: '03_performance-capture_features_faces.webm',
  scale: '04_performance-capture_features_scale.webm',
  'hands-wrists-fingers': '05_performance-capture_features_hands.webm',
  'level-changes': '06_performance-capture_features_level-changes.webm',
};

const generationClips: Record<string, string> = {
  'text-to-motion': '01_motion-generation_features_text_to_motion.webm',
  'batch-generation': '02_motion-generation_features_batch-generation.webm',
  looping: '03_motion-generation_features_looping.webm',
  'duration-control': '04_motion-generation_features_duration-control.webm',
  'curve-smoothing': '05_motion-generation_features_curve-smoothing.webm',
};

const editorClips: Record<string, string> = {
  'direct-with-poses': '01_motion-editor_features_direct_with_pose.webm',
  'direct-with-paths': '02_motion-editor_features_direct_with_paths.webm',
  'combine-motions': '04_motion-editor_features_combine-motions.webm',
  'character-swap': '05_motion-editor_features_character-swap.webm',
  'non-destructive-workflow': '06_motion-editor_features_nondestructive.webm',
};

const cartographerClips: Record<string, string> = {
  'auto-retarget': '01_cartographer_features_auto-retarget.webm',
  'map-any-motion': '02_cartographer_features_map-any-motion.webm',
  'facial-retargeting': '03_cartographer_features_face-retarget.webm',
  'second-limb-physics': '04_cartographer_features_second-limb-physics.webm',
  'collision-offsets': '06_cartographer_features_collision-offsets.webm',
};

const captureFeatureMedia: Record<string, MarketingMedia> = {
  'batch-capture': approvedImage(
    '07_performance-capture_features_batch-capture.png',
    'Batch Capture upload list.',
  ),
  realtime: approvedVideo(
    '08_performance-capture_features_realtime.webm',
    'Realtime capture demonstration.',
  ),
};

const editorFeatureMedia: Record<string, MarketingMedia> = {
  'generate-new-takes': approvedImage(
    '03_motion-editor_features_generate-new-takes.png',
    'Generate new takes in Performance Editor.',
  ),
};

const cartographerFeatureMedia: Record<string, MarketingMedia> = {
  'robot-ready': approvedImage(
    '05__cartographer_features_robot-ready.png',
    'Robot ready MuJoCo motion export.',
  ),
};

function featureMedia(
  clips: Record<string, string>,
  id: string,
  title: string,
  overrides: Record<string, MarketingMedia> = {},
) {
  const description = `${title} feature demonstration.`;
  if (overrides[id]) return overrides[id];
  const filename = clips[id];
  return filename
    ? approvedVideo(filename, description)
    : { type: 'empty' as const, description };
}

const generationFeatures = [
  [
    'text-to-motion',
    'Text-to-motion',
    'Generate smooth performances from a prompt',
  ],
  [
    'batch-generation',
    'Batch generation',
    'Produce multiple motions and variations at once for crowds or libraries',
  ],
  ['looping', 'Looping', 'Auto-generate seamless loops for cycles and idles'],
  [
    'duration-control',
    'Duration control',
    'Set the length, Swing generates to fit',
  ],
  [
    'curve-smoothing',
    'Curve smoothing',
    'Clean editable curves out of the box',
  ],
] as const;

const editorFeatures = [
  [
    'direct-with-poses',
    'Direct with poses',
    'Pin or adjust a pose, and the motion reshapes to honor it.',
  ],
  [
    'direct-with-paths',
    'Direct with paths',
    'Redraw a trajectory, and the performance follows.',
  ],
  [
    'generate-new-takes',
    'Generate new takes',
    'Regenerate a section or take instead of hand-fixing it.',
  ],
  [
    'combine-motions',
    'Combine Motions',
    'Combine motions in the timeline and generate smooth transitions',
  ],
  [
    'character-swap',
    'Character Swap',
    'Swap in any character in your Cartographer library',
  ],
  [
    'non-destructive-workflow',
    'Non-destructive workflow',
    'Jump to any version in your history without manually saving',
  ],
] as const;

export const performanceCaptureContent = {
  id: 'performance-capture',
  label: 'Performance Capture',
  description:
    'Powered by Comic, Performance Capture turns any video into production-ready motion. Choose the model that fits the shot, capture from any camera, and process detailed performances, up to 100 clips at once.',
  descriptionEmphasis: 'Comic',
  heroWidth: 'standard',
  actionLabel: 'Try Performance Capture',
  features: captureFeatures.map(([id, title, description]) => ({
    id,
    title,
    description,
    media: featureMedia(captureClips, id, title, captureFeatureMedia),
  })),
  statement:
    'Mocap on tap. Any video or camera into your motion pipeline in minutes.',
  statementAccent: 'Mocap on tap.',
  specs: captureSpecifications,
  actionSection: {
    title: 'Try Performance Capture',
    description:
      'Try it via the Cartwheel MCP or in the Performance Capture app.',
  },
  actionCards: [
    {
      id: 'capture-mcp',
      title: 'Cartwheel MCP',
      buttonLabel: 'Try the MCP',
      action: 'mcp',
      media: approvedImage(
        '09_performance-capture_try_mcp.png',
        'Cartwheel MCP integrations.',
      ),
    },
    {
      id: 'capture-app',
      title: 'Performance Capture',
      buttonLabel: 'Try the app',
      action: 'performance-capture',
      media: approvedVideo(
        '10_performance-capture_try_performance-capture.webm',
        'Performance Capture app demonstration.',
      ),
    },
  ],
  actionLayout: 'two-cards',
} satisfies ProductPageContent;

export const motionGenerationContent = {
  id: 'motion-generation',
  label: 'Performance Generation',
  description:
    'Powered by Swing, Performance Generation creates human performances from a text prompt in seconds. Describe the action, style, and timing, generate in batches, and pull the results straight into your animation or motion pipeline.',
  descriptionEmphasis: 'Swing',
  heroWidth: 'standard',
  actionLabel: 'Try Performance Generation',
  features: generationFeatures.map(([id, title, description]) => ({
    id,
    title,
    description,
    media: featureMedia(generationClips, id, title),
  })),
  statement:
    'Motion on demand. Create a performance, at any volume, from a line of text.',
  statementAccent: 'Motion on demand.',
  specs: generationSpecifications,
  actionSection: {
    title: 'Try Performance Generation',
    description:
      'Try it via the Cartwheel API or in the\nPerformance Generation app.',
  },
  actionCards: [
    {
      id: 'generation-mcp',
      title: 'Cartwheel MCP',
      buttonLabel: 'Try the MCP',
      action: 'mcp',
      media: approvedImage(
        '06_motion-generation_try_mcp.png',
        'Cartwheel MCP integrations.',
      ),
    },
    {
      id: 'generation-app',
      title: 'Performance Generation',
      buttonLabel: 'Try the app',
      action: 'motion-generation',
      media: approvedVideo(
        '07_motion-generation_try_performance-generation.webm',
        'Performance Generation app demonstration.',
      ),
    },
  ],
  actionLayout: 'two-cards',
} satisfies ProductPageContent;

export const motionEditorContent = {
  id: 'motion-editor',
  label: 'Performance Editor',
  description:
    'Powered by Swing, Performance Editor lets you modify and direct motion instead of keyframing it. Adjust poses and paths, and Performance Editor regenerates the performance to hit your marks.',
  descriptionEmphasis: 'Swing',
  heroWidth: 'standard',
  actionLabel: 'Try Performance Editor',
  features: editorFeatures.map(([id, title, description]) => ({
    id,
    title,
    description,
    media: featureMedia(editorClips, id, title, editorFeatureMedia),
  })),
  statement:
    'Direct more, keyframe less. Edit anything with poses, paths, and prompts.',
  statementAccent: 'Direct more, keyframe less.',
  specs: editorSpecifications,
  actionSection: {
    title: 'Try Performance Editor',
    description:
      'Try it via the Cartwheel MCP or in the Performance Editor app.',
  },
  actionCards: [
    {
      id: 'editor-mcp',
      title: 'Cartwheel MCP',
      buttonLabel: 'Try the MCP',
      action: 'mcp',
      media: approvedImage(
        '07_motion-editor_try_mcp.png',
        'Cartwheel MCP integrations.',
      ),
    },
    {
      id: 'editor-app',
      title: 'Performance Editor',
      buttonLabel: 'Try the app',
      action: 'motion-editor',
      media: approvedVideo(
        '08_motion-editor_try_performance-editor.webm',
        'Performance Editor app demonstration.',
      ),
    },
  ],
  actionLayout: 'two-cards',
} satisfies ProductPageContent;

export const cartographerContent = {
  id: 'cartographer',
  label: 'Cartographer',
  description:
    'Cartographer maps any motion, body and face, onto your character. Upload a rig and it recognizes the body automatically, then retarget anything from your library — captured, generated, or from the Cartwheel motion library — and export. Universal retargeting to 3D characters, robots, and avatars.',
  heroWidth: 'wide',
  actionLabel: 'Try Cartographer',
  features: [
    [
      'auto-retarget',
      'Auto-retarget',
      'Upload a character and Cartographer retargets automatically',
    ],
    [
      'map-any-motion',
      'Map any motion',
      'Map any motion from your library to any biped you have',
    ],
    [
      'facial-retargeting',
      'Facial retargeting',
      'Drive facial performance through blendshape retargets',
    ],
    [
      'second-limb-physics',
      'Second limb physics',
      'Add spring physics to wings, tails, and other appendages',
    ],
    [
      'robot-ready',
      'Robot Ready',
      'Retarget then export clean MuJoCo data to Robot models via API',
    ],
    [
      'collision-offsets',
      'Collision offsets',
      'Use offsets to avoid self-collisions',
    ],
  ].map(([id, title, description]) => ({
    id,
    title,
    description,
    media: featureMedia(cartographerClips, id, title, cartographerFeatureMedia),
  })),
  statement: 'Any character, performed by humans, from dragons to robots.',
  statementAccent: 'Any character, performed by humans,',
  actionCards: [
    {
      id: 'cartographer-trial',
      title: 'Try Cartographer',
      buttonLabel: 'Try the app',
      action: 'cartographer',
      media: undefined,
    },
  ],
  actionLayout: 'no-media',
} satisfies ProductPageContent;
